import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './LoginScreen.module.css';
import { UserService } from '../../api/services/UserService';
import { useTranslation } from '../../i18n/provider';

/**
 * TODO.md J1 — forgot-password request form.
 *
 * Always shows the same generic success message on any 200 response, never trying to infer
 * whether the email matched an account - the backend's anti-enumeration contract is meaningless
 * if the frontend then leaks the same information some other way (e.g. only showing a "check your
 * email" state for real accounts).
 */
export const ForgotPasswordScreen: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const userService = new UserService();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await userService.forgotPassword(email);
      setSubmitted(true);
    } catch (err: any) {
      // A genuine transport/server failure (e.g. rate limited, backend unreachable) - distinct
      // from the endpoint's own always-200 anti-enumeration response, which never reaches here.
      setError(err.message || t('forgotPassword.error'));
    } finally {
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className={styles.loginScreen}>
        <div className={styles.loginContainer}>
          <div className={styles.loginIcon}>📧</div>
          <h1 className={styles.loginTitle}>{t('forgotPassword.title')}</h1>
          <p className={styles.loginMessage} data-testid="forgot-password-success-message">
            {t('forgotPassword.successMessage')}
          </p>
          <Link to="/login" className={styles.toggleButton} data-testid="forgot-password-back-link">
            {t('forgotPassword.backToLogin')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.loginScreen}>
      <div className={styles.loginContainer}>
        <div className={styles.loginIcon}>🔑</div>
        <h1 className={styles.loginTitle}>{t('forgotPassword.title')}</h1>
        <p className={styles.loginMessage}>{t('forgotPassword.prompt')}</p>
        <form onSubmit={handleSubmit} className={styles.authForm}>
          {error && (
            <div className={styles.errorMessage} data-testid="forgot-password-error">
              {error}
            </div>
          )}
          <input
            type="email"
            placeholder={t('forgotPassword.emailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={styles.authInput}
            required
            disabled={isLoading}
            data-testid="forgot-password-email-input"
          />
          <button
            type="submit"
            className={`${styles.loginButton} ${styles.loginButtonPrimary}`}
            disabled={isLoading}
            data-testid="forgot-password-submit-button"
          >
            {isLoading ? t('auth.pleaseWait') : t('forgotPassword.submit')}
          </button>
        </form>
        <div className={styles.toggleAuth}>
          <Link to="/login" className={styles.toggleButton} data-testid="forgot-password-back-link">
            {t('forgotPassword.backToLogin')}
          </Link>
        </div>
      </div>
    </div>
  );
};
